/**
 * Central Route Permission Configuration
 * 
 * This file defines all routes and their required permissions in one place,
 * making it easy to manage and maintain access control across the application.
 */

import { Permission } from '../utils/rolePermissions';

export interface RoutePermissionConfig {
    path: string;
    requiredPermissions?: Permission[];
    requireAny?: boolean; // If true, user needs ANY of the permissions. If false, user needs ALL permissions.
    description?: string;
}

/**
 * Centralized route permission configuration
 * Routes not listed here are accessible to all authenticated users
 */
export const ROUTE_PERMISSIONS: RoutePermissionConfig[] = [
    // Dashboard Routes
    {
        path: '/',
        requiredPermissions: [Permission.VIEW_DASHBOARD],
        description: 'Main dashboard'
    },
    {
        path: '/dashboard',
        requiredPermissions: [Permission.VIEW_DASHBOARD],
        description: 'Dashboard page'
    },

    // Application Routes
    {
        path: '/work-items',
        requiredPermissions: [Permission.VIEW_WORK_ITEMS],
        description: 'Work items list'
    },
    {
        path: '/leads',
        requiredPermissions: [Permission.VIEW_LEADS],
        description: 'Leads management'
    },
    {
        path: '/leads/:leadId',
        requiredPermissions: [Permission.VIEW_LEADS, Permission.EDIT_APPLICATION],
        requireAny: true,
        description: 'Lead details and editing'
    },
    {
        path: '/loan-application/new',
        requiredPermissions: [Permission.CREATE_APPLICATION],
        description: 'Create new loan application'
    },
    {
        path: '/loan-application/edit/:id',
        requiredPermissions: [Permission.EDIT_APPLICATION],
        description: 'Edit loan application'
    },
    {
        path: '/pending-submission',
        requiredPermissions: [Permission.VIEW_APPLICATIONS],
        description: 'Pending submissions'
    },
    {
        path: '/applications',
        requiredPermissions: [Permission.VIEW_APPLICATIONS],
        description: 'All applications'
    },
    {
        path: '/application/:id',
        requiredPermissions: [Permission.VIEW_APPLICATIONS],
        description: 'Application details'
    },

    // Document Management Routes
    {
        path: '/document-management/:taskId',
        requiredPermissions: [Permission.MANAGE_DOCUMENTS],
        description: 'Document management'
    },
    {
        path: '/document-upload/:taskId',
        requiredPermissions: [Permission.UPLOAD_DOCUMENTS],
        description: 'Document upload'
    },
    {
        path: '/loan-application-documents/:id',
        requiredPermissions: [Permission.VIEW_DOCUMENTS],
        description: 'Loan application documents'
    },
    {
        path: '/loan-application-documents/view/:id',
        requiredPermissions: [Permission.VIEW_DOCUMENTS],
        description: 'View loan application documents'
    },
    {
        path: '/documents',
        requiredPermissions: [Permission.VIEW_DOCUMENTS],
        description: 'Document archive'
    },
    {
        path: '/documents/in-progress',
        requiredPermissions: [Permission.VIEW_DOCUMENTS],
        description: 'Applications collecting documents'
    },
    {
        path: '/documents/files/:customerId',
        requiredPermissions: [Permission.VIEW_DOCUMENTS],
        description: 'Archive: borrower file'
    },
    {
        path: '/documents/files/:customerId/customer',
        requiredPermissions: [Permission.VIEW_DOCUMENTS],
        description: 'Archive: customer documents'
    },
    {
        path: '/documents/files/:customerId/loans',
        requiredPermissions: [Permission.VIEW_DOCUMENTS],
        description: 'Archive: loan folders'
    },
    {
        path: '/documents/loans/:applicationId',
        requiredPermissions: [Permission.VIEW_DOCUMENTS],
        description: 'Archive: loan folder'
    },

    // Loan Processing Routes
    {
        path: '/verification',
        requiredPermissions: [Permission.PERFORM_VERIFICATION],
        description: 'Verification dashboard'
    },
    {
        path: '/verification/:applicationId',
        requiredPermissions: [Permission.PERFORM_VERIFICATION],
        description: 'Verification details'
    },
    {
        path: '/credit-assessment',
        requiredPermissions: [Permission.PERFORM_CREDIT_ASSESSMENT],
        description: 'Credit assessment dashboard'
    },
    {
        path: '/credit-assessment/:applicationId',
        requiredPermissions: [Permission.PERFORM_CREDIT_ASSESSMENT],
        description: 'Credit assessment details'
    },
    {
        path: '/credit-approval/:taskId',
        requiredPermissions: [Permission.APPROVE_LOANS],
        description: 'Credit Committee approval'
    },
    {
        path: '/collateral-verification',
        requiredPermissions: [Permission.VERIFY_COLLATERAL],
        description: 'Collateral verification dashboard'
    },
    {
        path: '/collateral-verification/:taskId',
        requiredPermissions: [Permission.VERIFY_COLLATERAL],
        description: 'Collateral verification details'
    },
    {
        path: '/loan-agreement',
        requiredPermissions: [Permission.MANAGE_LOAN_AGREEMENT],
        description: 'Loan agreement dashboard'
    },
    {
        path: '/loan-agreement/:taskId',
        requiredPermissions: [Permission.MANAGE_LOAN_AGREEMENT],
        description: 'Loan agreement details'
    },
    {
        path: '/disbursements',
        requiredPermissions: [Permission.MANAGE_DISBURSEMENTS],
        description: 'Disbursements dashboard'
    },
    {
        path: '/disbursements/:taskId',
        requiredPermissions: [Permission.MANAGE_DISBURSEMENTS],
        description: 'Disbursement details'
    },

    // Collections Routes
    {
        path: '/collections/due',
        requiredPermissions: [Permission.VIEW_DUE_COLLECTIONS],
        description: 'Due collections'
    },
    {
        path: '/collections/overdue',
        requiredPermissions: [Permission.VIEW_OVERDUE_COLLECTIONS],
        description: 'Overdue collections'
    },
    {
        path: '/collections',
        requiredPermissions: [Permission.VIEW_COLLECTIONS],
        description: 'All collections'
    },
    {
        path: '/collections/payment/:id',
        requiredPermissions: [Permission.RECORD_PAYMENTS],
        description: 'Record payment'
    },
    {
        // Same permission as a normal payment: a split is one receipt captured across several
        // loans, not a more privileged operation.
        path: '/collections/split-payment',
        requiredPermissions: [Permission.RECORD_PAYMENTS],
        description: 'Record a payment split across several loans'
    },
    {
        path: '/loan-accounts/:id',
        requiredPermissions: [Permission.VIEW_ACCOUNTS, Permission.VIEW_COLLECTIONS],
        requireAny: true,
        description: 'Loan account details'
    },
    {
        path: '/collections/penalties',
        requiredPermissions: [Permission.MANAGE_PENALTIES],
        description: 'Pending penalties'
    },
    {
        path: '/collections/penalties/:loanAccountId',
        requiredPermissions: [Permission.MANAGE_PENALTIES],
        description: 'Loan account penalties'
    },
    {
        path: '/collections/payment-summary',
        requiredPermissions: [Permission.VIEW_COLLECTIONS],
        description: 'Payment summary'
    },
    {
        path: '/collections/payment-overview',
        requiredPermissions: [Permission.VIEW_COLLECTIONS],
        description: 'Payment overview'
    },

    // Loan Book Routes
    {
        path: '/loan-book/summary',
        requiredPermissions: [Permission.VIEW_PORTFOLIO_SUMMARY],
        description: 'Portfolio summary'
    },
    {
        path: '/loan-book/overview',
        requiredPermissions: [Permission.VIEW_PORTFOLIO_OVERVIEW],
        description: 'Portfolio overview'
    },

    // Reports Routes
    {
        path: '/reports',
        requiredPermissions: [Permission.VIEW_REPORTS],
        description: 'Reports dashboard'
    },
    {
        path: '/reports/decline-reasons',
        requiredPermissions: [Permission.VIEW_REPORTS],
        description: 'Decline reasons report'
    },

    // Admin Routes
    {
        path: '/users',
        requiredPermissions: [Permission.MANAGE_USERS],
        description: 'User management'
    },
    {
        path: '/settings',
        requiredPermissions: [Permission.SYSTEM_SETTINGS],
        description: 'System settings'
    },
    {
        path: '/settings/:section',
        requiredPermissions: [Permission.SYSTEM_SETTINGS],
        description: 'System settings - individual section'
    },
    {
        path: '/notifications',
        requiredPermissions: [Permission.VIEW_NOTIFICATIONS],
        description: 'Notifications'
    },
    {
        path: '/communication',
        requiredPermissions: [Permission.VIEW_NOTIFICATIONS],
        description: 'Communication — notification history inbox'
    },
    {
        path: '/admin/route-permissions',
        requiredPermissions: [Permission.SYSTEM_SETTINGS],
        description: 'Route Permission Manager (Admin Only)'
    },

    // Customer Management Routes
    {
        path: '/birthdays',
        requiredPermissions: [Permission.VIEW_CUSTOMERS],
        description: 'Client birthdays'
    },
    {
        path: '/loan-customers',
        requiredPermissions: [Permission.VIEW_CUSTOMERS],
        description: 'Customer list'
    },
    {
        path: '/loan-customers/new',
        requiredPermissions: [Permission.CREATE_CUSTOMER],
        description: 'Create new customer'
    },
    {
        path: '/loan-customers/:id/edit',
        requiredPermissions: [Permission.EDIT_CUSTOMER],
        description: 'Edit customer'
    },
    {
        path: '/loan-customers/:id/view',
        requiredPermissions: [Permission.VIEW_CUSTOMERS],
        description: 'View customer details'
    },
    {
        path: '/loan-customers/:id/documents',
        requiredPermissions: [Permission.VIEW_CUSTOMER_DOCUMENTS],
        description: 'Customer documents'
    },

    { path: '/individual-customers', requiredPermissions: [Permission.VIEW_CUSTOMERS], description: 'Individual customers' },
    { path: '/individual-customers/new', requiredPermissions: [Permission.CREATE_CUSTOMER], description: 'Create individual customer' },
    { path: '/individual-customers/:id/edit', requiredPermissions: [Permission.EDIT_CUSTOMER], description: 'Edit individual customer' },
    { path: '/individual-customers/:id/view', requiredPermissions: [Permission.VIEW_CUSTOMERS], description: 'View individual customer' },
    { path: '/individual-customers/:id/documents', requiredPermissions: [Permission.VIEW_CUSTOMER_DOCUMENTS], description: 'Individual customer documents' },
    { path: '/individual-customers/:customerId/opening-balance', requiredPermissions: [Permission.EDIT_CUSTOMER], description: 'Individual opening balance' },

    // Account Management Routes
    {
        path: '/accounts',
        requiredPermissions: [Permission.VIEW_ACCOUNTS],
        description: 'Loan accounts'
    },
    {
        path: '/accounts/create-direct',
        requiredPermissions: [Permission.CREATE_LOAN_ACCOUNT],
        description: 'Create direct loan account'
    },
    {
        path: '/customers/:customerNumber/accounts',
        requiredPermissions: [Permission.VIEW_ACCOUNTS, Permission.VIEW_CUSTOMERS],
        requireAny: true,
        description: 'Customer accounts'
    }
];

/**
 * Helper function to get route permission config by path
 */
export const getRoutePermissions = (path: string): RoutePermissionConfig | undefined => {
    // First try exact match
    const exactMatch = ROUTE_PERMISSIONS.find(route => route.path === path);
    if (exactMatch) return exactMatch;

    // Then try pattern matching for dynamic routes
    return ROUTE_PERMISSIONS.find(route => {
        if (!route.path.includes(':')) return false;
        
        const routePattern = route.path.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${routePattern}$`);
        return regex.test(path);
    });
};

/**
 * Helper function to check if a route requires specific permissions
 */
export const routeRequiresPermissions = (path: string): boolean => {
    const config = getRoutePermissions(path);
    return config?.requiredPermissions !== undefined && config.requiredPermissions.length > 0;
};

/**
 * Get all routes grouped by permission category for administrative purposes
 */
export const getRoutesByCategory = () => {
    const categories = {
        dashboard: ROUTE_PERMISSIONS.filter(r => r.path.includes('dashboard') || r.path === '/'),
        applications: ROUTE_PERMISSIONS.filter(r => r.path.includes('application') || r.path.includes('leads') || r.path.includes('work-items')),
        loanProcessing: ROUTE_PERMISSIONS.filter(r => 
            r.path.includes('verification') || 
            r.path.includes('credit-assessment') || 
            r.path.includes('collateral') || 
            r.path.includes('loan-agreement') || 
            r.path.includes('disbursements')
        ),
        collections: ROUTE_PERMISSIONS.filter(r => r.path.includes('collections') || r.path.includes('penalties')),
        loanBook: ROUTE_PERMISSIONS.filter(r => r.path.includes('loan-book')),
        customers: ROUTE_PERMISSIONS.filter(r => r.path.includes('loan-customers') || r.path.includes('individual-customers')),
        accounts: ROUTE_PERMISSIONS.filter(r => r.path.includes('accounts')),
        documents: ROUTE_PERMISSIONS.filter(r => r.path.includes('documents')),
        reports: ROUTE_PERMISSIONS.filter(r => r.path.includes('reports')),
        admin: ROUTE_PERMISSIONS.filter(r => 
            r.path.includes('users') || 
            r.path.includes('settings') || 
            r.path.includes('notifications')
        )
    };

    return categories;
};
